import { Activity, Building2, ClipboardCheck, Coins, Download, FileText, Gift, HandCoins, History, Home, LayoutDashboard, Link2, Mail, Package, PackageCheck, QrCode, Settings, Store, TrendingUp, Truck, type LucideIcon, UserRoundCog, Users, Warehouse, Boxes } from "lucide-react";
import {
  DEFAULT_ENABLED_TIERS,
  MODULE_FEATURE,
  grantOf,
  isConfigurableRole,
  nodeOf,
  roleKey,
  rolesBelow,
  type OrgRolesDto,
  type PermissionModule,
  type SessionUser,
} from "@gatiq/shared";

/**
 * The sidebar, built from what the signed-in person may do (D-16) rather than
 * a fixed list per role — a sales rep given "view karigars" gets the same
 * Karigars page the admin uses.
 *
 * Icons are part of the nav data rather than looked up in the shell, so adding
 * a section means touching exactly one place. `:orgSlug` is substituted at
 * render time.
 *
 * Hiding an item is presentation only — the API enforces access itself.
 */
export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /**
   * Sidebar group heading. Consecutive items sharing one are drawn under a
   * single label; items without one sit at the top, ungrouped.
   */
  section?: string;
}

interface OrgNavItem extends NavItem {
  /** Shown to anyone who may view this module. */
  module?: PermissionModule;
  /** The org admin's alone — settings, which include who may do what. */
  admin?: true;
  /** Only when the org uses this tier of the chain. */
  tier?: string;
}

type NavUser = Pick<SessionUser, "role" | "orgSlug" | "permissions" | "level">;

const NETWORK = "Network";
const REWARDS = "Rewards";
const ORGANISATION = "Organisation";

const SUPER_ADMIN: NavItem[] = [
  { to: "/super-admin/organizations", label: "Organizations", icon: Building2 },
  { to: "/super-admin/settings", label: "Settings", icon: Settings },
  { to: "/super-admin/audit", label: "Audit log", icon: History },
  { to: "/super-admin/health", label: "System health", icon: Activity },
];

const KARIGAR: NavItem[] = [{ to: "/:orgSlug/karigar", label: "Home", icon: Home }];

/** The org console. Everyone in the chain sees the parts their role was granted. */
const CONSOLE: OrgNavItem[] = [
  { to: "/:orgSlug/admin", label: "Overview", icon: LayoutDashboard, module: "overview" },
  { to: "/:orgSlug/admin/performance", label: "Performance Tracker", icon: TrendingUp, module: "performance" },
  { to: "/:orgSlug/admin/depots", label: "Depots", icon: Warehouse, section: NETWORK, module: "depots", tier: "depot" },
  {
    to: "/:orgSlug/admin/sales-reps",
    label: "Sales reps",
    icon: UserRoundCog,
    section: NETWORK,
    module: "team",
    tier: "sales_rep",
  },
  {
    to: "/:orgSlug/admin/distributors",
    label: "Distributors",
    icon: Truck,
    section: NETWORK,
    module: "team",
    tier: "distributor",
  },
  {
    to: "/:orgSlug/admin/sub-distributors",
    label: "Sub-distributors",
    icon: Truck,
    section: NETWORK,
    module: "team",
    tier: "sub_distributor",
  },
  { to: "/:orgSlug/admin/counters", label: "Counters", icon: Store, section: NETWORK, module: "counters", tier: "counter" },
  { to: "/:orgSlug/admin/karigars", label: "Karigars", icon: Users, section: NETWORK, module: "karigars", tier: "karigar" },
  { to: "/:orgSlug/admin/approvals", label: "Approvals", icon: ClipboardCheck, section: NETWORK, module: "approvals" },
  { to: "/:orgSlug/admin/stock", label: "Stock", icon: Boxes, section: NETWORK, module: "dispatch" },
  { to: "/:orgSlug/admin/products", label: "Products", icon: Package, section: REWARDS, module: "products" },
  { to: "/:orgSlug/admin/documents", label: "Documents", icon: FileText, section: REWARDS, module: "documents" },
  { to: "/:orgSlug/admin/rewards", label: "Reward codes", icon: QrCode, section: REWARDS, module: "rewards" },
  { to: "/:orgSlug/admin/rewards/schemes", label: "Reward schemes", icon: Gift, section: REWARDS, module: "rewards" },
  { to: "/:orgSlug/admin/dispatch", label: "Dispatch", icon: PackageCheck, section: REWARDS, module: "dispatch" },
  { to: "/:orgSlug/admin/payouts", label: "Payouts", icon: Coins, section: REWARDS, module: "payouts" },
  { to: "/:orgSlug/admin/settlements", label: "Settlements", icon: HandCoins, section: REWARDS, module: "settlements" },
  { to: "/:orgSlug/admin/links", label: "Links", icon: Link2, section: ORGANISATION, module: "links" },
  { to: "/:orgSlug/admin/settings", label: "Settings", icon: Settings, section: ORGANISATION, admin: true },
  { to: "/:orgSlug/admin/audit", label: "Audit log", icon: History, section: ORGANISATION, module: "audit" },
  { to: "/:orgSlug/admin/broadcasts", label: "Promotions", icon: Mail, section: ORGANISATION, module: "broadcasts", admin: true },
  /* No `module`, no `admin` — every role sees it. Installing is not a
     permission; it is a thing anyone holding a phone might want to do. */
  { to: "/:orgSlug/admin/install", label: "Install app", icon: Download, section: ORGANISATION },
];

/** A counter keeps its own shop screens; the console adds whatever else it was granted. */
const COUNTER_HOME: OrgNavItem[] = [
  { to: "/:orgSlug/counter", label: "Dashboard", icon: LayoutDashboard },
  { to: "/:orgSlug/counter/karigars", label: "My karigars", icon: Users, module: "karigars" },
  { to: "/:orgSlug/counter/redemptions", label: "Payouts", icon: Coins, module: "payouts" },
  { to: "/:orgSlug/counter/qr", label: "Registration QR", icon: QrCode },
  { to: "/:orgSlug/counter/install", label: "Install app", icon: Download },
];

/**
 * Which team pages someone scoped to their own chain would find anyone on:
 * the roles that report to theirs in the chain (Sales Rep 2 under a
 * Distributor puts Sales reps in a distributor's reach), and a distributor's
 * sub-distributors.
 */
function teamTierInReach(tier: string, user: NavUser, roles: OrgRolesDto | null): boolean {
  if (grantOf(user.permissions, "team", "view") === "org") return true;
  if (user.role === "distributor" && tier === "sub_distributor") return true;
  if (!roles || !isConfigurableRole(user.role)) return false;
  return rolesBelow(roles.chain, roleKey(user.role, user.level)).some((key) => nodeOf(key).tier === tier);
}

export function navFor(user: NavUser, roles: OrgRolesDto | null): NavItem[] {
  const slug = user.orgSlug ?? "";
  const withSlug = ({ to, label, icon, section }: NavItem): NavItem => ({
    to: to.replace(":orgSlug", slug),
    label,
    icon,
    section,
  });

  if (user.role === "super_admin") return SUPER_ADMIN;
  if (user.role === "karigar") return KARIGAR.map(withSlug);

  const tiers: string[] = roles?.enabledTiers ?? DEFAULT_ENABLED_TIERS;
  const allowed = (item: OrgNavItem): boolean =>
    (item.admin ? user.role === "org_admin" : !item.module || Boolean(grantOf(user.permissions, item.module, "view"))) &&
    (!item.tier || tiers.includes(item.tier)) &&
    (item.module !== "team" || !item.tier || teamTierInReach(item.tier, user, roles)) &&
    // A switched-off module (D-20) is not in the menu at all.
    (!item.module || !MODULE_FEATURE[item.module] || Boolean(roles?.features?.[MODULE_FEATURE[item.module]!]));

  const items =
    user.role === "counter"
      ? [
          ...COUNTER_HOME.filter(allowed),
          // Karigars and payouts already have the counter's own screens above.
          ...CONSOLE.filter((i) => i.module !== "karigars" && i.module !== "payouts" && i.to !== "/:orgSlug/admin/install" && allowed(i)),
        ]
      : CONSOLE.filter(allowed);
  return items.map(withSlug);
}
